import { NotificationService, StakingOrigin, useIntl } from '@capsicum/common';
import {
	ActivityIndicator,
	BackgroundView,
	BottomActions,
	Goat,
	Label,
	Snackbar,
	StatsBar,
	TextButton,
	useRouter,
	useWebPush,
	VoteDistribution
} from '@capsicum/common-web';
import { AccessIcon, BindingIcon, CalendarIcon, CappedIcon } from '@capsicum/common-web/icons';
import { ApprovedFooter, HeaderWarning } from '@capsicum/features/regulations/web';
import { useSurveyDetails } from '@capsicum/features/survey/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { StakeFanTokenChip, useBuySellButtons, useFanTokenStakingNavigation } from '@capsicum/socios-core-web';
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { useSurveyVoteFlow } from '../../hooks/useSurveyVoteFlow';
import { SurveyAnswerGroup, SurveyBanner } from '../../ui';

type SurveyDetailsProps = { partnerId: string; surveyId: string };

export const SurveyDetails = ({ partnerId, surveyId }: SurveyDetailsProps) => {
	const { $t } = useIntl();
	const { push } = useRouter();
	const { requestPermissionAfterAction } = useWebPush();

	const {
		currencyId,
		currencyShortName,
		custodialHasEnoughBalance,
		dateLabelProps,
		hasUserVoted,
		information,
		informationLink,
		intlAboutThisPoll,
		intlAnswers,
		intlBinding,
		intlCappedAtTokenLimit,
		// TRANSLATIONS
		intlClubWillAbideResult,
		intlSubmitVote,
		intlTokenAmountNeeded,
		intlTokenBuyLink,
		intlVoteDistribution,
		introductionText,
		isBound,
		isCapped,
		isError,
		// GET request(s)
		isLoading,
		isNCWFlow,
		isNCWUserVotePendingBlockchain,
		isSurveyClosed,
		isSurveyVotable,
		// CALLBACKS
		onAnswerChange,
		partnerBg,
		partnerLogo,
		partnerName,
		// STATE
		selectedAnswer,
		sendFanTokenStakingEvent,
		showTokenNeededLink,
		showVoteDistribution,
		showVotesCollected,
		surveyQuestion,
		voteDistributionData,
		votesCollectedData
	} = useSurveyDetails({ surveyId });

	const { buy, isBuyEnabled, isLoading: isBuySellButtonsLoading } = useBuySellButtons(currencyId || '');
	const { isStakeAllowed, navToFanTokenStake, userCurrentStake } = useFanTokenStakingNavigation(
		currencyId as string,
		{ origin: StakingOrigin.VOTES, originIds: [partnerId, surveyId] },
		true // start the navigation with the hidden navigation flow
	);

	const { getTokenBalanceBy } = useNCWBalance();

	const { isCustodialVoteError, isCustodialVoteProcessing, isCustodialVoteSuccessful, onSubmitHandler } =
		useSurveyVoteFlow({
			isNCWFlow,
			partnerId,
			surveyId
		});

	/**
	 * Show success notification when survey is submitted successfully
	 */
	useEffect(() => {
		if (!$t || !isCustodialVoteSuccessful) {
			return;
		}

		NotificationService.addNotification({
			component: (
				<Snackbar
					description=""
					testID="surveyDetails-success-notification"
					title={$t('survey.toast.success.topText')}
					variant="success"
				/>
			)
		});
		requestPermissionAfterAction({ title: $t('pushNotifications.prompt.pollTitle') });
	}, [$t, isCustodialVoteSuccessful, requestPermissionAfterAction]);

	/**
	 * Show error notification when survey submission returns an error
	 */
	useEffect(() => {
		if (!$t || !isCustodialVoteError) {
			return;
		}

		NotificationService.addNotification({
			component: (
				<Snackbar
					description={$t('survey.toast.error.topText')}
					testID="surveyDetails-error-notification"
					title={$t('survey.toast.error.bottomText')}
					variant="error"
				/>
			)
		});
	}, [$t, isCustodialVoteError]);

	const onBuyFanToken = useCallback(() => {
		if (isBuySellButtonsLoading) return;

		if (!isBuyEnabled) {
			push(`/partner/${partnerId}`);

			return;
		}

		buy();
	}, [buy, push, partnerId, isBuyEnabled, isBuySellButtonsLoading]);

	const ncwBalances = currencyId ? getTokenBalanceBy(b => b.currencyId === currencyId) : undefined;
	const walletBalance = Number(ncwBalances?.amount || 0);

	const isStakeNeeded = Boolean(isNCWFlow && isStakeAllowed && Number(userCurrentStake) < 1);
	const navigateToStakePage = () => isStakeNeeded && navToFanTokenStake();

	const hasEnoughBalance = Boolean(isNCWFlow ? walletBalance > 0 : custodialHasEnoughBalance);

	const userCanVote = Boolean(
		isNCWFlow
			? isSurveyVotable && !isStakeNeeded && !isNCWUserVotePendingBlockchain
			: isSurveyVotable && !isCustodialVoteProcessing && hasEnoughBalance
	);

	const showSubmitButton = Boolean(
		isNCWFlow ? isSurveyVotable && !isNCWUserVotePendingBlockchain : isSurveyVotable && !isCustodialVoteSuccessful
	);

	const isButtonDisabled = Boolean(
		isNCWFlow
			? !isSurveyVotable || isStakeNeeded || !selectedAnswer || isNCWUserVotePendingBlockchain
			: !isSurveyVotable || isCustodialVoteProcessing || !selectedAnswer || !hasEnoughBalance
	);

	const buyOrStakeChip = useMemo(() => {
		if (!hasEnoughBalance) {
			return (
				<S.LabelContainer>
					<Label
						icon={AccessIcon}
						label={intlTokenAmountNeeded}
						testID="surveyDetails-buyFanToken"
						variant="outlined"
					>
						{showTokenNeededLink && (
							<TextButton
								color="brand"
								testID="surveyDetails-buyFanToken-button"
								text={intlTokenBuyLink}
								textSize="small"
								onClick={onBuyFanToken}
							/>
						)}
					</Label>
				</S.LabelContainer>
			);
		}

		if (isStakeNeeded && currencyId) {
			return (
				<S.LabelContainer>
					<StakeFanTokenChip
						currencyShortName={currencyShortName || ''}
						testId="surveyDetails-stakeFanToken-button"
						onClickAction={() => {
							sendFanTokenStakingEvent();
							navigateToStakePage();
						}}
					/>
				</S.LabelContainer>
			);
		}

		return;
	}, [hasEnoughBalance, isNCWFlow, isStakeNeeded, onBuyFanToken]);

	if (isLoading) {
		return (
			<S.ActivityIndicatorContainer>
				<ActivityIndicator />
			</S.ActivityIndicatorContainer>
		);
	}

	if (isError) {
		return <Goat image="ankward" testID="surveyDetails-errorGoat-image" title={$t('survey.details.error')} />;
	}

	return (
		<BackgroundView src={partnerBg}>
			<S.SurveyUkHeaderWarning horizontalPadding page="poll"></S.SurveyUkHeaderWarning>
			{partnerName && (
				<SurveyBanner introductionText={introductionText} partnerLogo={partnerLogo} partnerName={partnerName} />
			)}
			<S.ScrollableSection>
				<S.Container>
					<S.ActionsContainer>
						{dateLabelProps && (
							<S.LabelContainer>
								<Label icon={CalendarIcon} {...dateLabelProps} />
							</S.LabelContainer>
						)}

						{(isBound || isCapped) && (
							<S.LabelContainer>
								{isBound && <Label icon={BindingIcon} label={intlBinding} variant="blue" />}
								{isCapped && <Label icon={CappedIcon} label={intlCappedAtTokenLimit} variant="blue" />}
							</S.LabelContainer>
						)}

						{surveyQuestion && <S.Question>{surveyQuestion}</S.Question>}

						{buyOrStakeChip}

						{intlAnswers && (
							<SurveyAnswerGroup
								answers={intlAnswers}
								hasUserVoted={hasUserVoted}
								isSurveyExpired={isSurveyClosed}
								isUserVotePendingBlockchain={isNCWUserVotePendingBlockchain}
								testID="surveyDetails-answerGroup-section"
								userCanVote={userCanVote}
								onAnswerChange={onAnswerChange}
							/>
						)}
					</S.ActionsContainer>

					{information && (
						<S.SectionContainer>
							<S.SectionTitle>{intlAboutThisPoll}</S.SectionTitle>
							<S.AboutCard>
								{isBound && <Label icon={BindingIcon} label={intlClubWillAbideResult} variant="blue" />}

								<S.InformationText>
									{information}
									{informationLink && (
										<S.InformationLink href={informationLink} target="_blank">
											{informationLink}
										</S.InformationLink>
									)}
								</S.InformationText>
							</S.AboutCard>
						</S.SectionContainer>
					)}

					{(showVoteDistribution || showVotesCollected) && (
						<S.SectionContainer>
							<S.SectionTitle>{intlVoteDistribution}</S.SectionTitle>

							{showVotesCollected && (
								<S.StatsBarCard>
									<StatsBar {...votesCollectedData} />
								</S.StatsBarCard>
							)}

							{showVoteDistribution && <VoteDistribution {...voteDistributionData} />}
						</S.SectionContainer>
					)}

					{userCanVote && <S.SectionContainer />}

					<S.UkApprovedFooter page="poll" />
				</S.Container>
			</S.ScrollableSection>

			{showSubmitButton && (
				<BottomActions
					buttonPropsArray={[
						{
							fullWidth: true,
							isLoading: isCustodialVoteProcessing,
							onClick: () => onSubmitHandler(selectedAnswer),
							testID: 'surveyDetails-submitVote-button',
							text: intlSubmitVote
						}
					]}
					disabled={isButtonDisabled}
				/>
			)}
		</BackgroundView>
	);
};

export namespace S {
	export const ActivityIndicatorContainer = styled.div`
		display: flex;
		width: 100%;
		height: 100%;
		justify-content: center;
		padding-top: ${({ theme }) => theme.spacings.xxxlarge};
	`;

	export const SurveyUkHeaderWarning = styled(HeaderWarning)`
		margin: 0 auto ${({ theme }) => theme.spacings.xsmall};
		z-index: 101;
		background-color: transparent;
		top: ${({ theme }) => theme.spacings.xsmall};
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			width: 560px;
			padding: ${({ theme }) => `0 ${theme.spacings.small}`};
		}
	`;

	export const UkApprovedFooter = styled(ApprovedFooter)`
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding-bottom: ${({ theme }) => theme.spacings.xxxlarge};
		}
	`;

	export const ScrollableSection = styled.div`
		width: 100%;
		padding-bottom: 64px;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		${({ theme }) => theme.border.radius.xlargeTop};

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `0 ${theme.spacings.small}`};
		}
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		width: 560px;
		margin: ${({ theme }) => `${theme.spacings.xlarge} auto 0`};
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			width: 100%;
		}
	`;

	export const ActionsContainer = styled.div`
		padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small}`};
		margin: ${({ theme }) => `0 0 ${theme.spacings.large}`};
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
	`;

	export const Question = styled.span`
		display: block;
		text-align: center;
		margin-bottom: ${({ theme }) => theme.spacings.medium};
		${({ theme }) => theme.fonts.headings.title6}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const LabelContainer = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		flex-wrap: wrap;
		gap: ${({ theme }) => theme.spacings.xsmall};
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const SectionContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
		margin: ${({ theme }) => `0 0 ${theme.spacings.large}`};
	`;

	export const SectionTitle = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const AboutCard = styled.div`
		padding: ${({ theme }) => theme.spacings.small};
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
	`;

	export const StatsBarCard = styled.div`
		padding: ${({ theme }) => `0 ${theme.spacings.small}`};
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
		background: ${({ theme }) => theme.colors.backgrounds.main.container};
		${({ theme }) => theme.border.radius.medium};
	`;

	export const InformationText = styled.span`
		display: block;
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const InformationLink = styled.a`
		display: block;
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;
}
